import { authenticatedFetch } from './auth';

/**
 * Frontend price calculation utilities
 * This provides a fallback calculation when backend is not available
 * and helper functions for price-related operations
 */

export interface Kruzok {
    id: string;
    name: string;
    price: number;
    isStretko: boolean;
    requiresZapisne?: boolean;
    type?: 'TRIH' | 'BERN';
}

export interface Person {
    id: string;
    schoolyearId?: string;
    gender?: string;
    isBernolakova?: boolean;
    schoolyear?: {
        type: 'HIGHSCHOOL' | 'UNIVERSITY' | 'OTHER';
    };
}

export interface Settings {
    baseStretko?: number;
    baseKruzok?: number;
    baseStretkoSsAnim?: number;
    baseKruzokSsAnim?: number;
    zapisneValue?: number;
    zapisneValueSsAnim?: number;
    VPvalue?: number;
    VPvalueSsAnim?: number;
    CPvalue?: number;
    CPvalueSsAnim?: number;
    bernKazdyDalsi?: number;
}

export interface PriceBreakdown {
    breakdown: Array<{
        type: 'kruzok' | 'zapisne' | 'vp_discount' | 'cp_discount' | 'admin_reduction' | 'bern_surcharge';
        name: string;
        kruzokPrice?: number;
        baseFee?: number;
        totalPrice?: number;
        amount?: number;
        isStretko?: boolean;
        bernSurcharge?: number;
    }>;
    totalPrice: number;
    useSsAnimPricing: boolean;
    metadata: {
        personType?: string;
        isAnimator?: boolean;
    };
}

/**
 * Helper function to determine if person should use SS/Anim pricing
 * Note: This is a simplified version for frontend use - backend has full logic
 */
export const shouldUseSsAnimPricing = (person: Person): boolean => {
    // Check if person's school year type is HIGHSCHOOL or UNIVERSITY
    if (person.schoolyear && (person.schoolyear.type === 'HIGHSCHOOL' || person.schoolyear.type === 'UNIVERSITY')) {
        return true;
    }

    // Note: We cannot check isanim table from frontend, so this is a best-effort check
    // The backend should always be used for accurate calculations
    return false;
};

/**
 * Helper function to determine if person should automatically get VP discount
 */
export const shouldAutoApplyVP = (person: Person): boolean => {
    // University students automatically get VP discount
    return person.schoolyear?.type === 'UNIVERSITY';
};

/**
 * Calculate total price with breakdown (fallback for when backend is unavailable)
 * This should match the backend calculation logic as closely as possible
 */
export const calculatePriceFallback = (
    selectedKruzkyIds: string[],
    kruzky: Kruzok[],
    person: Person,
    vzdelavacyPoukaz: boolean,
    cestnePrehlasenie: boolean,
    settings: Settings
): number => {
    const useSsAnimPricing = shouldUseSsAnimPricing(person);

    // Sum up kruzky prices with base fees
    const selectedKruzky = selectedKruzkyIds.map(id => kruzky.find(k => k.id === id)).filter(Boolean) as Kruzok[];
    const bernKruzky = selectedKruzky.filter(k => k.type === 'BERN');
    let totalBernSurcharge = 0;

    const kruzkyTotal = selectedKruzky.reduce((total, kruzok, index) => {
        // Add kruzok price
        let kruzokTotal = kruzok.price;

        // Add base fee based on isStretko and pricing variant
        if (useSsAnimPricing) {
            // Use SS/Anim variant pricing
            if (kruzok.isStretko && settings?.baseStretkoSsAnim !== undefined) {
                kruzokTotal += settings.baseStretkoSsAnim;
            } else if (!kruzok.isStretko && settings?.baseKruzokSsAnim !== undefined) {
                kruzokTotal += settings.baseKruzokSsAnim;
            }
        } else {
            // Use standard pricing
            if (kruzok.isStretko && settings?.baseStretko) {
                kruzokTotal += settings.baseStretko;
            } else if (!kruzok.isStretko && settings?.baseKruzok) {
                kruzokTotal += settings.baseKruzok;
            }
        }

        // Calculate BERN surcharge for every BERN kruzok except the first one
        if (kruzok.type === 'BERN') {
            const bernIndex = bernKruzky.findIndex(k => k.id === kruzok.id);
            if (bernIndex > 0) { // Not the first BERN kruzok
                totalBernSurcharge += settings?.bernKazdyDalsi || 0;
            }
        }

        return total + kruzokTotal;
    }, 0);

    // Add the total BERN surcharge to the kruzky total
    const finalKruzkyTotal = kruzkyTotal + totalBernSurcharge;

    // Add zapisne (registration fee) only if any selected kruzok requires it
    const requiresZapisne = selectedKruzky.some(kruzok => kruzok.requiresZapisne === true);

    // Use appropriate zapisne value
    const zapisne = requiresZapisne ?
        (useSsAnimPricing && settings?.zapisneValueSsAnim !== undefined ?
            settings.zapisneValueSsAnim :
            (settings?.zapisneValue || 0)) : 0;

    // Calculate discounts based on VP/CP selections and pricing variant
    // University students automatically get VP discount
    const autoApplyVP = shouldAutoApplyVP(person);
    const vpDiscount = (vzdelavacyPoukaz || autoApplyVP) ?
        (useSsAnimPricing && settings?.VPvalueSsAnim !== undefined ?
            settings.VPvalueSsAnim :
            (settings?.VPvalue || 0)) : 0;

    const cpDiscount = cestnePrehlasenie ?
        (useSsAnimPricing && settings?.CPvalueSsAnim !== undefined ?
            settings.CPvalueSsAnim :
            (settings?.CPvalue || 0)) : 0;

    // Calculate total: kruzky + zapisne - VP discount - CP discount
    // Ensure the total doesn't go below 0
    return Math.max(0, finalKruzkyTotal + zapisne - vpDiscount - cpDiscount);
};

/**
 * Fetch price breakdown from backend API
 */
export const fetchPriceBreakdown = async (
    personId: string,
    kruzkyIds: string[],
    vzdelavacyPoukaz: boolean,
    cestnePrehlasenie: boolean,
    prihlaskaId?: string,
    isAdminMode = false
): Promise<PriceBreakdown | null> => {
    try {
        const fetchPrice = isAdminMode ? authenticatedFetch : fetch;
        const endpoint = isAdminMode ? '/api/admin/calculate-price' : '/api/prihlaska/calculate-price';
        const response = await fetchPrice(`${process.env.REACT_APP_PATH}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                personId,
                kruzkyIds,
                vzdelavacyPoukaz,
                cestnePrehlasenie,
                prihlaskaId
            })
        });

        if (!response.ok) {
            throw new Error('Failed to calculate price');
        }

        const data = await response.json();
        return data.data;
    } catch (error) {
        console.error('Error fetching price breakdown:', error);
        return null;
    }
};

/**
 * Get total price - tries backend first, falls back to local calculation
 */
export const getTotalPrice = async (
    selectedKruzkyIds: string[],
    kruzky: Kruzok[],
    person: Person,
    vzdelavacyPoukaz: boolean,
    cestnePrehlasenie: boolean,
    settings: Settings
): Promise<number> => {
    // Try to get from backend first
    const backendBreakdown = await fetchPriceBreakdown(
        person.id,
        selectedKruzkyIds,
        vzdelavacyPoukaz,
        cestnePrehlasenie
    );

    if (backendBreakdown) {
        return backendBreakdown.totalPrice;
    }

    // Fallback to local calculation
    return calculatePriceFallback(
        selectedKruzkyIds,
        kruzky,
        person,
        vzdelavacyPoukaz,
        cestnePrehlasenie,
        settings
    );
};

/**
 * Filter kruzky based on person compatibility
 */
export const filterCompatibleKruzky = (allKruzky: Kruzok[], person: Person): Kruzok[] => {
    return allKruzky.filter(kruzok => {
        // Filter by gender compatibility
        const genderCompatible = (kruzok as any).gender === 'BOTH' ||
            (kruzok as any).gender === person.gender ||
            !person.gender; // If person has no gender set, show all

        // Filter by schoolyear compatibility
        let schoolyearCompatible = true;
        if (person.schoolyearId && (kruzok as any).schoolYear && (kruzok as any).schoolYear.length > 0) {
            // If both person and kruzok have schoolyear info, check compatibility
            schoolyearCompatible = (kruzok as any).schoolYear.some((sy: any) => sy.id === person.schoolyearId);
        }
        // If either person has no schoolyear or kruzok has no schoolyear restrictions, allow it

        // Filter by Bernolákova school compatibility
        // If person is a KID and doesn't attend Bernolákova school, filter out BERN type kruzky
        const bernolakCompatible = !(!person.isBernolakova && (kruzok as any).type === 'BERN');

        return genderCompatible && schoolyearCompatible && bernolakCompatible;
    });
};
